import { useEffect, useState } from "react";

import type { Product } from "../types/marketplace";
import { formatCurrency, formatNumber } from "../utils/formatters";

interface PriceComparisonProps {
  openRequestKey?: number;
  products: Product[];
  onClear: () => void;
  onProductSelect: (product: Product) => void;
  onRemove: (product: Product) => void;
}

function getBestPrice(products: Product[]): number {
  return Math.min(...products.map((product) => product.price));
}

function categoryText(product: Product): string {
  return product.categories?.slice(0, 2).join(", ") || "Sin categoría";
}

function stockText(product: Product): string {
  const stock = product.stock ?? 0;

  return stock > 0 ? `${formatNumber(stock)} disponibles` : "Sin stock";
}

function PriceComparison({
  openRequestKey = 0,
  onClear,
  onProductSelect,
  onRemove,
  products
}: PriceComparisonProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (products.length < 2) {
      setIsOpen(false);
    }
  }, [products.length]);

  useEffect(() => {
    if (openRequestKey > 0 && products.length >= 2) {
      setIsOpen(true);
    }
  }, [openRequestKey, products.length]);

  if (products.length === 0) {
    return null;
  }

  const bestPrice = getBestPrice(products);

  return (
    <>
      <aside className="compare-tray">
        <div>
          <strong>Comparador de precios</strong>
          <small>
            {products.length < 2
              ? "Suma otro producto para comparar"
              : `${formatNumber(products.length)} productos seleccionados`}
          </small>
        </div>

        <div className="compare-tray-items">
          {products.map((product) => (
            <button
              className="compare-tray-item"
              key={product.id ?? product.name}
              onClick={() => onProductSelect(product)}
              type="button"
            >
              <img src={product.image} alt={product.name} decoding="async" loading="lazy" />
            </button>
          ))}
        </div>

        <button
          className="compare-primary-button"
          disabled={products.length < 2}
          onClick={() => setIsOpen(true)}
          type="button"
        >
          Ver comparación
        </button>

        <button className="compare-clear-button" onClick={onClear} type="button">
          Limpiar
        </button>
      </aside>

      {isOpen && (
        <div className="compare-overlay" role="dialog" aria-modal="true">
          <section className="compare-panel">
            <header className="compare-panel-header">
              <div>
                <small>Comparador</small>
                <h2>Comparar y comprar mejor</h2>
              </div>
              <button onClick={() => setIsOpen(false)} type="button">
                x
              </button>
            </header>

            <div className="compare-table">
              {products.map((product) => {
                const isBestPrice = product.price === bestPrice;

                return (
                  <article className="compare-column" key={product.id ?? product.name}>
                    {isBestPrice && <span className="best-price-badge">Mejor precio</span>}

                    <img src={product.image} alt={product.name} decoding="async" loading="lazy" />
                    <h3>{product.name}</h3>
                    <p className="compare-store">{product.clientName ?? "Tienda"}</p>

                    <div className="compare-price">
                      <strong>{formatCurrency(product.price)}</strong>
                      {product.oldPrice && <del>{formatCurrency(product.oldPrice)}</del>}
                    </div>

                    <dl>
                      <div>
                        <dt>Descuento</dt>
                        <dd>{product.discount ?? "Sin descuento"}</dd>
                      </div>
                      <div>
                        <dt>Stock</dt>
                        <dd>{stockText(product)}</dd>
                      </div>
                      <div>
                        <dt>Categoría</dt>
                        <dd>{categoryText(product)}</dd>
                      </div>
                    </dl>

                    <button
                      className="compare-view-button"
                      onClick={() => {
                        setIsOpen(false);
                        onProductSelect(product);
                      }}
                      type="button"
                    >
                      Comprar
                    </button>

                    <button
                      className="compare-remove-button"
                      onClick={() => onRemove(product)}
                      type="button"
                    >
                      Quitar
                    </button>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default PriceComparison;
